import type { PairwiseOverlap, TripleOverlap } from "@/types/overlap";

const COLORS = [
  { fill: "#3b82f6", stroke: "#2563eb" }, // blue
  { fill: "#ef4444", stroke: "#dc2626" }, // red
  { fill: "#22c55e", stroke: "#16a34a" }, // green
  { fill: "#f59e0b", stroke: "#d97706" }, // amber
];

// Concept names live in an HTML legend below the SVG — SVG <text> can't wrap or
// truncate, so long names collided on top of each other near the center.
function VennLegend({ items }: { items: { name: string; color: string }[] }) {
  return (
    <div className="mt-2 flex w-full flex-wrap justify-center gap-x-3 gap-y-1">
      {items.map((it) => (
        <span
          key={it.name}
          className="inline-flex max-w-[45%] items-center gap-1.5 text-[11px] text-muted-foreground"
          title={it.name}
        >
          <span
            className="size-2 shrink-0 rounded-full"
            style={{ backgroundColor: it.color }}
          />
          <span className="truncate">{it.name}</span>
        </span>
      ))}
    </div>
  );
}

interface VennTwoProps {
  pair: PairwiseOverlap;
  colorA?: number;
  colorB?: number;
  width?: number;
  height?: number;
}

export function VennTwo({
  pair,
  colorA = 0,
  colorB = 1,
  width = 300,
  height = 200,
}: VennTwoProps) {
  const cx = width / 2;
  const cy = height / 2;
  const r = height * 0.35;
  const offset = r * 0.6;

  const cA = COLORS[colorA % COLORS.length];
  const cB = COLORS[colorB % COLORS.length];

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: width }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        data-testid="venn-two"
        role="img"
        aria-label={`Venn diagram: ${pair.concept_a_name} (${pair.only_a_count} only), ${pair.concept_b_name} (${pair.only_b_count} only), ${pair.shared_count} shared`}
      >
        <circle
          cx={cx - offset}
          cy={cy}
          r={r}
          fill={cA.fill}
          fillOpacity={0.25}
          stroke={cA.stroke}
          strokeWidth={2}
        />
        <circle
          cx={cx + offset}
          cy={cy}
          r={r}
          fill={cB.fill}
          fillOpacity={0.25}
          stroke={cB.stroke}
          strokeWidth={2}
        />
        {/* Only-A label */}
        <text
          x={cx - offset - r * 0.4}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          className="text-xs font-semibold"
          fill={cA.stroke}
        >
          {pair.only_a_count}
        </text>
        {/* Shared label */}
        <text
          x={cx}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          className="text-xs font-bold text-foreground"
          fill="currentColor"
        >
          {pair.shared_count}
        </text>
        {/* Only-B label */}
        <text
          x={cx + offset + r * 0.4}
          y={cy}
          textAnchor="middle"
          dominantBaseline="central"
          className="text-xs font-semibold"
          fill={cB.stroke}
        >
          {pair.only_b_count}
        </text>
      </svg>
      <VennLegend
        items={[
          { name: pair.concept_a_name, color: cA.stroke },
          { name: pair.concept_b_name, color: cB.stroke },
        ]}
      />
    </div>
  );
}

interface VennThreeProps {
  pairs: PairwiseOverlap[];
  tripleOverlaps: TripleOverlap[];
  conceptNames: string[];
  width?: number;
  height?: number;
}

export function VennThree({
  pairs,
  tripleOverlaps,
  conceptNames,
  width = 350,
  height = 300,
}: VennThreeProps) {
  const cx = width / 2;
  const cy = height / 2 - 10;
  const r = height * 0.28;
  const dist = r * 0.65;

  // Position circles at 120° intervals (top, bottom-left, bottom-right)
  const positions = [
    { x: cx, y: cy - dist }, // top
    { x: cx - dist * Math.cos(Math.PI / 6), y: cy + dist * Math.sin(Math.PI / 6) }, // bottom-left
    { x: cx + dist * Math.cos(Math.PI / 6), y: cy + dist * Math.sin(Math.PI / 6) }, // bottom-right
  ];

  // Build lookup for pairwise counts
  const pairLookup: Record<string, number> = {};
  for (const p of pairs) {
    const key1 = `${p.concept_a_name}|${p.concept_b_name}`;
    const key2 = `${p.concept_b_name}|${p.concept_a_name}`;
    pairLookup[key1] = p.shared_count;
    pairLookup[key2] = p.shared_count;
  }

  const tripleCount = tripleOverlaps.length > 0 ? tripleOverlaps[0].count : 0;

  // Pairwise intersection label positions (midpoint between circle centers)
  const pairPositions = [
    { x: (positions[0].x + positions[1].x) / 2, y: (positions[0].y + positions[1].y) / 2 },
    { x: (positions[0].x + positions[2].x) / 2, y: (positions[0].y + positions[2].y) / 2 },
    { x: (positions[1].x + positions[2].x) / 2, y: (positions[1].y + positions[2].y) / 2 },
  ];

  // Get pairwise shared counts (excluding triple)
  const getPairCount = (a: string, b: string) => {
    const total = pairLookup[`${a}|${b}`] || 0;
    return total - tripleCount; // Subtract triple overlap to show only pairwise-exclusive
  };

  return (
    <div className="flex flex-col items-center" style={{ maxWidth: width }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        width={width}
        height={height}
        data-testid="venn-three"
        role="img"
        aria-label={`Venn diagram: ${conceptNames.join(", ")}`}
      >
        {positions.map((pos, i) => (
          <circle
            key={i}
            cx={pos.x}
            cy={pos.y}
            r={r}
            fill={COLORS[i].fill}
            fillOpacity={0.2}
            stroke={COLORS[i].stroke}
            strokeWidth={2}
          />
        ))}
        {/* Triple overlap center */}
        {tripleCount > 0 && (
          <text
            x={cx}
            y={cy}
            textAnchor="middle"
            dominantBaseline="central"
            className="text-xs font-bold text-foreground"
            fill="currentColor"
          >
            {tripleCount}
          </text>
        )}
        {/* Pairwise intersections */}
        {conceptNames.length >= 3 && (
          <>
            <text
              x={pairPositions[0].x}
              y={pairPositions[0].y}
              textAnchor="middle"
              dominantBaseline="central"
              className="text-[10px] font-medium text-foreground"
              fill="currentColor"
            >
              {getPairCount(conceptNames[0], conceptNames[1])}
            </text>
            <text
              x={pairPositions[1].x}
              y={pairPositions[1].y}
              textAnchor="middle"
              dominantBaseline="central"
              className="text-[10px] font-medium text-foreground"
              fill="currentColor"
            >
              {getPairCount(conceptNames[0], conceptNames[2])}
            </text>
            <text
              x={pairPositions[2].x}
              y={pairPositions[2].y}
              textAnchor="middle"
              dominantBaseline="central"
              className="text-[10px] font-medium text-foreground"
              fill="currentColor"
            >
              {getPairCount(conceptNames[1], conceptNames[2])}
            </text>
          </>
        )}
      </svg>
      <VennLegend
        items={conceptNames.map((name, i) => ({
          name,
          color: COLORS[i % COLORS.length].stroke,
        }))}
      />
    </div>
  );
}
